import { createAsyncThunk } from '@reduxjs/toolkit';
import { UploadTypes } from 'types/app/upload';
import {
  IBatch,
  IClone,
  ICreate,
  IEmailRequest,
  IGet,
  IList,
  IReportList,
  IUpdate,
  IUpload,
} from '../actions/admin/data';
import { ICopy, ILink, ILinkResponse, IMove, IReset } from '../actions/admin/diagram';
import { IUpdateState } from '../actions/product/state';
import { AxiosWrapper } from './axios.wrapper';
import { IAIModule, IModuleDAO } from 'types/domain/module';
import { ICourseNodeDAO } from 'types/domain/course';
import { EntityTypes } from 'constants/entities';
import { ISceneDAO } from 'types/domain/scene';
import { RootState } from 'types';
import { editLanguage } from './editLanguage';

export const getRequest = createAsyncThunk('api/admin/get', async (data: IGet, { getState }) => {
  const languageId = editLanguage(getState() as RootState, data.type);
  const response = await AxiosWrapper.post('/api/admin/get', languageId ? { ...data, languageId } : data);
  return response.data;
});
export const listRequest = createAsyncThunk('api/admin/list', async (data: IList) => {
  const response = await AxiosWrapper.post('/api/admin/list', data);
  return response.data;
});

export const updateRequest = createAsyncThunk('api/admin/update', async (data: IUpdate) => {
  const response = await AxiosWrapper.post('/api/admin/update', data);
  return response.data;
});

export const batchRequest = createAsyncThunk('api/admin/batch', async (data: IBatch) => {
  const response = await AxiosWrapper.post('/api/admin/batch', data);
  return response.data;
});

export const createRequest = createAsyncThunk('api/admin/create', async (data: ICreate) => {
  const response = await AxiosWrapper.post('/api/admin/create', data);
  return response.data;
});

export const copyRequest = createAsyncThunk('api/admin/copy', async (data: ICopy) => {
  const response = await AxiosWrapper.post('/api/admin/copy', data);
  return response.data;
});

export const cloneRequest = createAsyncThunk('api/admin/clone', async (data: IClone) => {
  const response = await AxiosWrapper.post('/api/admin/clone', data);
  return response.data;
});

export const linkRequest = createAsyncThunk('api/admin/link', async (data: ILink) => {
  const response = await AxiosWrapper.post('/api/admin/link', data);
  return response.data as ILinkResponse;
});

export const moveRequest = createAsyncThunk('api/admin/move', async (data: IMove) => {
  const response = await AxiosWrapper.post('/api/admin/move', data);
  return response.data;
});

export const resetRequest = createAsyncThunk('api/admin/reset', async (data: IReset) => {
  const response = await AxiosWrapper.post('/api/admin/reset', data);
  return response.data;
});

export const stateRequest = createAsyncThunk('api/admin/state', async (data: Partial<IUpdateState>) => {
  const response = await AxiosWrapper.post('/api/admin/state', data);
  return response.data;
});

export const sendInviteRequest = createAsyncThunk('api/user/sendinvite', async (data: IUpdate) => {
  const response = await AxiosWrapper.post('/api/user/sendinvite', data);
  return response.data;
});

export const sendEmailRequest = createAsyncThunk('api/admin/email', async (data: IEmailRequest) => {
  const response = await AxiosWrapper.post('/api/admin/email', data);
  return response.data;
});

export const signedUrlRequest = createAsyncThunk('api/admin/upload', async (data: IUpload) => {
  const response = await AxiosWrapper.post(
    // A learner's own uploads — ID checks, and answers to a request — go through
    // the learner route; the admin one is for authors.
    [UploadTypes.VERIFICATION_REQUEST, UploadTypes.ORG_USER_VARIABLE].includes(data.type)
      ? '/api/learner/upload'
      : '/api/admin/upload',
    data,
  );
  return response.data;
});

export const reportRequest = createAsyncThunk('api/report/list', async (data: IReportList) => {
  const response = await AxiosWrapper.post('/api/report/list', data);
  return response.data;
});

export const reportExportRequest = createAsyncThunk('api/report/export', async (data: IReportList) => {
  const response = await AxiosWrapper.downloadBlobFromPost('/api/report/export', data);
  return response.data;
});

export const getResponsesRequest = createAsyncThunk('api/report/responses', async (data: IList) => {
  const response = await AxiosWrapper.post('/api/report/responses', data);
  return response.data;
});

export const createAIModuleRequest = createAsyncThunk('/api/admin/ai-module', async (data: IAIModule) => {
  const response = await AxiosWrapper.post('/api/admin/ai-module', data);
  return response.data;
});

export const importModuleRequest = createAsyncThunk<
  IModuleDAO | ICourseNodeDAO,
  { file: File; offlineId: number; courseId?: number; parentEntityId?: number; parentEntityAttribute?: string }
>('api/module/import', async ({ file, offlineId, courseId, parentEntityId, parentEntityAttribute }) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('offlineId', String(offlineId));
  if (courseId !== undefined) formData.append('parentCourseId', String(courseId));
  if (parentEntityId !== undefined) {
    formData.append('parentEntityId', String(parentEntityId));
    formData.append('parentEntityType', EntityTypes.COURSE_NODE);
  }
  if (parentEntityAttribute !== undefined) formData.append('parentEntityAttribute', parentEntityAttribute);
  const response = await AxiosWrapper.post('/api/module/import', formData);

  return { ...response.data, offlineId };
});

export interface IAiImageError {
  message: string;
}

/**
 * Worst case is ~30s generating plus up to 90s processing. The server's own budget is
 * OpenAI:AiImageJobTimeoutSeconds; this sits above it so the server's error message
 * wins over a client-side abort.
 */
const AI_IMAGE_TIMEOUT_MS = 150 * 1000;

/**
 * Generates an image for a slide and returns the finished scene — renditions included,
 * renderable on arrival. `scene.actionId` records which slide it was generated for, but
 * the action is not touched: putting the image on the slide is a separate save.
 *
 * Image scenes come back as PROCESSING and stay there — only the video path writes
 * ACTIVE — so arrival, not status, means ready.
 */
export const generateActionAiImageRequest = createAsyncThunk<
  ISceneDAO,
  { actionId: number; prompt: string; useCurrentSceneAsReference?: boolean },
  { rejectValue: IAiImageError }
>('api/module/action/ai-image', async ({ actionId, prompt, useCurrentSceneAsReference }, { rejectWithValue }) => {
  try {
    const response = await AxiosWrapper.post(
      `/api/module/action/${actionId}/ai-image`,
      { prompt, useCurrentSceneAsReference: !!useCurrentSceneAsReference },
      { timeout: AI_IMAGE_TIMEOUT_MS },
    );

    return response.data as ISceneDAO;
  } catch (error: any) {
    const status = error?.response?.status;
    // A 500 carries the generation or processing failure from the job itself.
    const detail =
      typeof error?.response?.data === 'string' ? error.response.data : error?.response?.data?.message || error.message;

    console.error('ai-image generation failed', status ?? 'no response', error);

    if (status === 403)
      return rejectWithValue({ message: 'You do not have permission to generate an image for this slide.' });

    if (status === 400) return rejectWithValue({ message: 'A prompt is required to generate an image.' });

    return rejectWithValue({ message: `Could not generate the image${detail ? `: ${detail}` : ''}` });
  }
});

export const getScormFiles = createAsyncThunk('scormfiles', async () => {
  const files = [
    'scorm/container/adlcp_rootv1p2.xsd',
    'scorm/container/ims_xml.xsd',
    'scorm/container/imscp_rootv1p1p2.xsd',
    'scorm/container/imsmanifest.xml',
    'scorm/container/imsmd_rootv1p2p1.xsd',
    'scorm/container/index.html',
    'scorm/container/index.js',
    'scorm/container/metadata.xml',
    'scorm/container/pipwerks.js',
  ];

  return Promise.all(files.map(AxiosWrapper.getBlob));
});
